import {inject, Injectable} from '@angular/core';
import {CommonsService} from '../core/commons.service';
import {SearchContainer} from '../core/definitions/search-container';
import {FilterGroupHierarchyNode, HierarchicFilterGroup, QueryMenu, SearchViewPath} from '../core/definitions/search-objects';
import {CmsApiService} from '../core/cms-api.service';
import {StoredSearch} from '../core/definitions/stored-search';
import {StoredSearchData} from '../core/definitions/stored-search-data';
import {LoggerService} from "../core/logger.service";
import {Subject} from 'rxjs';
import {SearchViewFilterService} from "./search-view-filter.service";

const FOLDER_CONTENT_PATHS = new Set([
  'home/folders/folder',
  'home/media_folders/folder',
]);

/** Folder contents are other object types, so a folders-view stored search must not apply. */
export function isFolderContentPath(path: string | null | undefined): boolean {
  return !!path && FOLDER_CONTENT_PATHS.has(path);
}

export class StoredSearchServiceImplementation {

  private readonly storedSearchParamKeys = [
    'path',
    'queryContainer.query',
    'queryContainer.selectedQueryMenu',
    'filtersFacets.checkedFilters',
    'filtersFacets.hierarchicFilterGroup',
    'order',
    'orderParentQuery',
    'searchResultViewName',
    'templateGroupId',
    'extendedSearchParams',
  ];
  public curStoredSearchId = '';

  constructor(private searchContainer: SearchContainer,
              private commons: CommonsService,
              private readonly cmsApi: CmsApiService,
              private logger: LoggerService,
              private searchViewFilterService: SearchViewFilterService) {
  }

  public async loadStoredSearches(): Promise<Array<StoredSearch>> {
    const storedSearches: Array<StoredSearch> = await this.cmsApi.getStoredSearchesForUser();
    this.searchContainer.storedSearch.storedSearches = storedSearches;
    this.searchContainer.storedSearch.unchangedStoredSearches = structuredClone(storedSearches);
    return storedSearches;
  }

  public getStoredSearches(): Array<StoredSearch> {
    return this.searchContainer.storedSearch.storedSearches || [];
  }

  public getStoredSearch(storedSearchId: string): StoredSearch | null {
    const storedSearches = this.getStoredSearches();
    const storedSearch: StoredSearch = storedSearches.find(f => f.storedSearchId === storedSearchId);
    return storedSearch ? storedSearch : null;
  }

  public getUnchangedStoredSearch(storedSearchId: string): StoredSearch | null {
    const storedSearch: StoredSearch = (this.searchContainer.storedSearch.unchangedStoredSearches || []).find(f => f.storedSearchId === storedSearchId);
    return storedSearch ? storedSearch : null;
  }

  public setCurrentStoredSearch(storedSearchOrId: string | StoredSearch): StoredSearch {
    let storedSearch: StoredSearch;
    if (typeof storedSearchOrId === 'object') {
      storedSearch = storedSearchOrId;
    } else {
      storedSearch = this.getStoredSearch(storedSearchOrId);
    }
    this.searchContainer.storedSearch.curStoredSearchId = storedSearch ? storedSearch.storedSearchId : null;
    this.searchContainer.storedSearch.curStoredSearch = storedSearch ? storedSearch : null;
    this.searchContainer.storedSearch.curStoredSearchName = storedSearch ? storedSearch.stored_search.name : null;
    if (!this.searchContainer.storedSearch.curStoredSearchId) {
      this.curStoredSearchId = null;
    }
    return storedSearch;
  }

  public getCurrentStoredSearch(): StoredSearch {
    return this.searchContainer.storedSearch.curStoredSearch;
  }

  public getDefaultStoredSearch(): StoredSearch | null {
    const storedSearch: StoredSearch = this.getStoredSearches().find(f => f.stored_search.isDefault);
    return storedSearch ? storedSearch : null;
  }

  public getCurrentOrDefaultStoredSearch(): StoredSearch | null {
    let storedSearch: StoredSearch = this.getCurrentStoredSearch();
    if (!storedSearch) {
      storedSearch = this.getDefaultStoredSearch();
    }
    return storedSearch;
  }

  public setStoredSearchPathParamsOnSearchContainer(pathParams: object): void {
    pathParams = pathParams || {};
    // Reset checkedFilters before applying the new stored search's params to prevent filters
    // from the previous stored search bleeding into the next one. The setObjectValueFromPath
    // call below merges object keys rather than replacing the whole object, so without this
    // reset any filter keys that exist in the old stored search but not in the new one would
    // survive the switch.
    this.searchContainer.filtersFacets.checkedFilters = {};
    for (const paramKey of this.storedSearchParamKeys) {
      if (paramKey === 'filtersFacets.hierarchicFilterGroup') {
        this.applyStoredHierarchicFilter(pathParams[paramKey]);
        continue;
      }
      if (paramKey === 'queryContainer.selectedQueryMenu') {
        this.applyStoredSelectedQueryMenu(pathParams[paramKey]);
        continue;
      }
      this.commons.setObjectValueFromPath(this.searchContainer, paramKey, pathParams[paramKey], true);
    }
  }

  public getSetStoredSearchPathParams(storedSearchId: string, path: string): Array<object> {
    const storedSearch: StoredSearch = this.getStoredSearch(storedSearchId);
    let pathParams: any;
    // home/folders/folder starts with home/folders, but a folder shows other object
    // types. Stored searches cannot be created there, so do not inherit the parent criteria.
    if (storedSearch && storedSearch.stored_search.pathParams && !isFolderContentPath(path)) {
      pathParams = storedSearch.stored_search.pathParams[path];
      if (!pathParams) {
        if (path.indexOf(storedSearch.stored_search.homePath) === 0) {
          pathParams = this.createStoredSearchPathParams(storedSearch.stored_search, path);
        }
      }
    }
    if (!pathParams) {
      this.setCurrentStoredSearch(null);
    }
    return pathParams;
  }

  public async createOrUpdateStoredSearch(storedSearch: StoredSearch, privateStoredSearch: boolean = true): Promise<StoredSearch> {
    if (storedSearch && storedSearch.stored_search) {
      if (this.nameOfStoredSearchAlreadyExists(storedSearch.storedSearchId, storedSearch.stored_search.name)) {
        throw Error('Name already exists'); // Rejects returned promise
      }
      storedSearch.private = privateStoredSearch;
      storedSearch.stored_search.stored = true;
      storedSearch.stored_search.pathParams[this.searchContainer.path] = this.getStoredSearchPathParamsFromSearchContainer();
      storedSearch.stored_search.homePath = storedSearch.stored_search.pathParams[this.searchContainer.path].path
      storedSearch.stored_search.path = storedSearch.stored_search.homePath;

      await this.cmsApi.createOrUpdateStoredSearch(storedSearch);
      const existingIds: Array<string> = this.getStoredSearches().map(f => f.storedSearchId);
      await this.loadStoredSearches();
      storedSearch = this.getStoredSearches().find(f => {
        return f.storedSearchId === storedSearch.storedSearchId || existingIds.indexOf(f.storedSearchId) === -1;
      });
    }
    return storedSearch;
  }

  public createNewStoredSearch(): StoredSearch {
    const storedSearch: StoredSearch = {
      private: true,
      stored_search: {
        name: '',
        isDefault: false,
        marked: true,
        homePath: this.searchContainer.path,
        pathParams: {},
        stored: false
      }
    };
    storedSearch.stored_search.pathParams[this.searchContainer.path] = null;
    return storedSearch;
  }

  public async storeStoredSearchAsNewStoredSearch(storedSearch: StoredSearch, newName: string, setAsDefault = false): Promise<StoredSearch> {
    if (storedSearch && storedSearch.storedSearchId && storedSearch.stored_search) {
      const originalId: string = storedSearch.storedSearchId;
      const storedOriginal: StoredSearch = this.copyOfStoredVersion(originalId);
      const newStoredSearch: StoredSearch = this.createCopyForSaveAs(storedSearch, newName, setAsDefault);

      const savedStoredSearch: StoredSearch = await this.createOrUpdateStoredSearch(newStoredSearch, newStoredSearch.private);
      if (storedOriginal && storedOriginal.stored_search.isDefault && newStoredSearch.stored_search.isDefault) {
        await this.clearDefaultFlagOfStoredSearch(storedOriginal);
      }
      return savedStoredSearch;
    }
  }

  public storedSearchHasChanges(storedSearch: StoredSearch): boolean {
    const excludeKeys: Array<keyof StoredSearch | keyof StoredSearchData> = [
      '$$optionNameWeighed',
      '$$weight',
      'settingMenu'
    ];

    const currentCopy: StoredSearch = JSON.parse(JSON.stringify(storedSearch));
    const storedCopy: StoredSearch = JSON.parse(JSON.stringify(this.getUnchangedStoredSearch(storedSearch.storedSearchId)));

    excludeKeys.forEach(key => {
      delete currentCopy[key];
      delete currentCopy.stored_search[key];
      if (storedCopy) {
        delete storedCopy[key];
        delete storedCopy.stored_search[key];
      }
    });

    // Use equals (not areObjectsEqual): areObjectsEqual only walks keys of the
    // first argument, so removing a filter key/value looks unchanged while adding
    // one is detected. equals compares key/array lengths both ways (P10-1560).
    let changed = !this.commons.equals(currentCopy, storedCopy);
    if (!changed) {
      const searchPathParams = this.getStoredSearchPathParamsFromSearchContainer(this.searchContainer.path);
      const storedSearchPathParams = storedSearch.stored_search.pathParams[this.searchContainer.path];
      changed = !this.commons.equals(searchPathParams, storedSearchPathParams);
    }
    return changed;
  }

  public async deleteStoredSearch(storedSearchId: string): Promise<void> {
    if (this.getCurrentStoredSearch() && this.getCurrentStoredSearch().storedSearchId === storedSearchId) {
      this.setCurrentStoredSearch(null);
    }

    try {
      await this.cmsApi.deleteStoredSearch(storedSearchId);
      await this.loadStoredSearches();
    } catch (e) {
      this.logger.error(`Failed to delete stored search ${storedSearchId}`, e);
      throw e;
    }
  }

  public toggleStoredSearchIsDefault(storedSearch: StoredSearch): void {
    if (!storedSearch) {
      return;
    }
    storedSearch = this.getStoredSearch(storedSearch.storedSearchId);
    if (storedSearch) {
      storedSearch.stored_search.isDefault = !storedSearch.stored_search.isDefault;
    }
  }

  private createCopyForSaveAs(storedSearch: StoredSearch, newName: string, setAsDefault: boolean): StoredSearch {
    const newStoredSearch: StoredSearch = JSON.parse(JSON.stringify(storedSearch));
    delete newStoredSearch.storedSearchId;
    newStoredSearch.stored_search.name = newName;
    newStoredSearch.stored_search.isDefault = setAsDefault;
    return newStoredSearch;
  }

  private copyOfStoredVersion(storedSearchId: string): StoredSearch | null {
    const stored: StoredSearch = this.getUnchangedStoredSearch(storedSearchId) || this.getStoredSearch(storedSearchId);
    return stored ? JSON.parse(JSON.stringify(stored)) : null;
  }

  private async clearDefaultFlagOfStoredSearch(storedOriginal: StoredSearch): Promise<void> {
    storedOriginal.stored_search.isDefault = false;
    await this.cmsApi.createOrUpdateStoredSearch(storedOriginal);
    await this.loadStoredSearches();
  }

  private nameOfStoredSearchAlreadyExists(storedSearchId: string, name: string): boolean {
    return this.getStoredSearches()
      .filter(f => f.storedSearchId !== storedSearchId)
      .map(f => f.stored_search.name)
      .indexOf(name) > -1;
  }

  private createStoredSearchPathParams(storedSearch: StoredSearchData, path: string): any {
    storedSearch.pathParams[path] = this.getStoredSearchPathParamsFromSearchContainer(path);
    storedSearch.pathParams[path].path = path;
    return storedSearch.pathParams[path];
  }

  private getStoredSearchPathParamsFromSearchContainer(path?: string): any {
    const res = {};
    for (const paramKey of this.storedSearchParamKeys) {
      if (paramKey === 'filtersFacets.hierarchicFilterGroup') {
        res[paramKey] = this.getSlimHierarchicFilterGroup(
          this.searchContainer.filtersFacets.hierarchicFilterGroup);
        continue;
      }
      let value = this.commons.getObjectValueFromPath(this.searchContainer, paramKey);
      if (value !== undefined) {
        value = this.commons.copy(value, {ignoreEmptyArray: true});
        let resVal = value;
        if (path && paramKey === 'filtersFacets.checkedFilters') {
          resVal = this.getValidCheckedFilters(value, path);
        }
        res[paramKey] = resVal;
      }
    }
    return res;
  }

  // Remove filters that are not relevant for the path view, e.g.
  // "object_type" if going from "home/artifacts" to "home/artifacts/design"
  private getValidCheckedFilters(filtersIn: {[name: string]: any[]}, path: string): {[name: string]: any[]} {
    const filters: {[name: string]: any[]} = {};
    if (!this.searchContainer.filtersFacets.filterGroups || this.curStoredSearchId !== this.searchContainer.storedSearch.curStoredSearchId) {
      this.searchViewFilterService.setCheckFilterGroupsFromSearchView(this.searchContainer);
      this.curStoredSearchId = this.searchContainer.storedSearch.curStoredSearchId;
    }
    if (Object.keys(filtersIn).length) {
      for (const [filterName, filterValues] of Object.entries(filtersIn)) {
        filters[filterName] = [...filterValues];
      }
    } else {
      this.logger.warn('No filters found in stored search');
    }
    const validFilters = {};
    const pathViews: Array<SearchViewPath> = this.commons.filter(this.searchContainer.searchView.paths, {path_name: path});
    if (pathViews && pathViews.length > 0) {
      this.setValidFilters(validFilters);
      for (const filterName in filters) {
        if (!validFilters[filterName]) {
          delete filters[filterName];
        }
      }
    }
    this.setEnabledFilterGroups(filters);
    return filters;
  }

  private setEnabledFilterGroups(filters: {[name: string]: any[]} = {}) {
    const filterGroups = this.searchContainer.filtersFacets.filterGroups;
    const filterNames = Object.keys(filters);
    for (const filterGroup of filterGroups) {
      const filterGroupFilterNames = filterGroup.filters.map(filter => filter.name);
      if (filterNames.some(filterName => filterGroupFilterNames.includes(filterName))) {
        filterGroup.enabled = true;
      }
    }
  }
  private setValidFilters(validFilters: {[name: string]: boolean} = {}) {
    const filterGroups = this.searchContainer.filtersFacets.filterGroups;
    if (filterGroups) {
      for (const filterGroup of filterGroups) {
        for (const filter of filterGroup.filters) {
          validFilters[filter.name] = true;
        }
      }
    }
  }

  private applyStoredSelectedQueryMenu(storedMenu: QueryMenu | undefined): void {
    // Replace rather than merge. selectedQueryMenu often points at a live item in the
    // query-menu list; merging would mutate that item (e.g. turn "general" into extended")
    // and make switching back to general search a no-op.
    if (this.searchContainer.queryContainer.selectedQueryMenu) {
      this.searchContainer.queryContainer.selectedQueryMenu.selected = false;
    }
    // For backward compatibility, treat "advanced" as "extended"
    if (storedMenu.query_type === 'advanced') {
      storedMenu.query_type = 'extended';
    }
    this.searchContainer.queryContainer.selectedQueryMenu = storedMenu
      ? this.commons.copy(storedMenu)
      : null;
  }

  private applyStoredHierarchicFilter(storedGroup: { lastSelectedNode?: FilterGroupHierarchyNode } | undefined): void {
    const lastSelectedNode = this.getSlimLastSelectedNode(storedGroup?.lastSelectedNode);
    const currentGroup = this.searchContainer.filtersFacets.hierarchicFilterGroup;
    if (currentGroup?.lastSelectedNode?.facet) {
      currentGroup.lastSelectedNode.facet.selected = false;
      currentGroup.lastSelectedNode.open = false;
    }
    this.deselectHierarchicNodes(currentGroup?.childrenArray);
    // Replace the live tree with a stub (or null) so a previous selection cannot bleed
    // into the next stored search, and so search can use lastSelectedNode.facet.id
    // before the hierarchy has been rebuilt from facets.
    this.searchContainer.filtersFacets.hierarchicFilterGroup = lastSelectedNode
      ? { lastSelectedNode } as HierarchicFilterGroup
      : null;
  }

  private deselectHierarchicNodes(nodes: FilterGroupHierarchyNode[] | undefined): void {
    if (!nodes) {
      return;
    }
    for (const node of nodes) {
      if (node.facet) {
        node.facet.selected = false;
      }
      node.open = false;
      this.deselectHierarchicNodes(node.childrenArray);
    }
  }

  private getSlimHierarchicFilterGroup(
    hierarchicFilterGroupIn: HierarchicFilterGroup | null | undefined
  ): { lastSelectedNode: FilterGroupHierarchyNode | null } {
    return { lastSelectedNode: this.getSlimLastSelectedNode(hierarchicFilterGroupIn?.lastSelectedNode) };
  }

  private getSlimLastSelectedNode(node: FilterGroupHierarchyNode | undefined): FilterGroupHierarchyNode | null {
    const id = node?.facet?.id;
    if (!id) {
      return null;
    }
    return {
      facet: {
        id,
        name: node.facet.name,
        shortName: node.facet.shortName,
        selected: true
      },
      open: true
    } as FilterGroupHierarchyNode;
  }
}

@Injectable({
  providedIn: 'root'
})
export class StoredSearchService {
  // Injections
  private readonly cmsApi: CmsApiService = inject(CmsApiService);
  private readonly commons: CommonsService = inject(CommonsService);
  private readonly logger: LoggerService = inject(LoggerService);
  private readonly searchViewFilterService: SearchViewFilterService = inject(SearchViewFilterService);

  private _openStoredSearchEditPanel$ = new Subject<boolean>();
  openStoredSearchEditPanel$ = this._openStoredSearchEditPanel$.asObservable();

  public openStoredSearchEditPanel() {
    this._openStoredSearchEditPanel$.next(true);
  }

  public createFSI(searchContainer: SearchContainer): StoredSearchServiceImplementation {
    return new StoredSearchServiceImplementation(searchContainer, this.commons, this.cmsApi, this.logger, this.searchViewFilterService);
  }

  /**
   *
   * @param {string} path
   * @param {SearchContainer} searchContainer
   * @return {boolean}
   */
  public checkPathParamsExists(path: string, searchContainer: SearchContainer): boolean {
    let res = false;
    if (searchContainer.storedSearch.curStoredSearchId) {
      for (const storedSearch of Object.values(searchContainer.storedSearch.curStoredSearch.stored_search.pathParams)) {
        if (storedSearch && storedSearch.path === path) {
          res = true;
          break;
        }
      }
    }
    return res;
  }
}
